import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { captureHandledException } from "src/services/sentry";

import { bulkUploadUserCoursesCertificate } from "../../api/certificates";
import { getComplianceWithCourses, type ComplianceWithCoursesResponseHydrated } from "../../api/compliance";
import { getCrewUsers } from "../../api/crew";
import { UserCoursesBulkUpload } from "../../components/CertificateUpload";
import Spinner from "../../components/Spinner";
import { deriveFormData, type DerivedFormData } from ".";

interface UserCoursesBulkUploadPageProps {
  match: { params: { crewId: string } };
  location: { search: string };
}

const getPreselectedUserId = (search: string): number | undefined => {
  const userId = Number(new URLSearchParams(search).get("userId"));
  return Number.isInteger(userId) && userId > 0 ? userId : undefined;
};

const UserCoursesBulkUploadPage = ({ match, location }: UserCoursesBulkUploadPageProps) => {
  const { t } = useTranslation();
  const crewId = Number(match.params.crewId);
  const preselectedUserId = getPreselectedUserId(location.search);

  const [formData, setFormData] = useState<DerivedFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!crewId) return;
    setLoading(true);
    Promise.all([getComplianceWithCourses(crewId), getCrewUsers(crewId)])
      .then(([complianceResponse, users]) => {
        setFormData(
          deriveFormData(complianceResponse.data as Record<string, ComplianceWithCoursesResponseHydrated>, users),
        );
      })
      .catch((err) => {
        captureHandledException(err, { feature: "userCoursesBulkCertificateUpload", action: "load", crewId });
        setError(t("certificate_upload.bulk.load_error"));
      })
      .finally(() => setLoading(false));
  }, [crewId, t]);

  const onSubmit = async (values: Parameters<typeof bulkUploadUserCoursesCertificate>[0]): Promise<string | null> => {
    try {
      const result = await bulkUploadUserCoursesCertificate(values);
      if (result?.status === "ok") {
        return null;
      }
      return result?.reason ?? t("certificate_upload.error_generic");
    } catch (err) {
      captureHandledException(err, { feature: "userCoursesBulkCertificateUpload", action: "submit", crewId });
      return t("certificate_upload.error_generic");
    }
  };

  return (
    <>
      <Typography variant="h4">{t("certificate_upload.bulk.single_user.title")}</Typography>
      {loading && <Spinner />}
      {error && (
        <Typography
          color="error"
          sx={{
            mt: 2,
          }}
        >
          {error}
        </Typography>
      )}
      {!loading && !error && formData && (
        <UserCoursesBulkUpload
          crewId={crewId}
          courses={formData.courses}
          users={formData.users}
          preselectedUserId={preselectedUserId}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
};

export default UserCoursesBulkUploadPage;
